import { createSelector } from '@reduxjs/toolkit'
import _groupBy from 'lodash/groupBy'
import _sortBy from 'lodash/sortBy'
import _cloneDeep from 'lodash/cloneDeep'

import { licensesDefaultSort } from 'features/licenses/utils'
import {
  makeFetchSelectors,
  makeTypedFetchSelectors,
} from 'app/redux-fetch/selectors'
import type { DomainType } from 'features/domains/types'
import type { LicenseType } from 'features/licenses/types'
import type {
  Home,
  HomeActiveConfig,
  homeActiveEnergyConfig,
  HomeSoftwareType,
  HomeSoftwareHistoryType,
  HostConfig,
  PriceV2,
  SubscriptionCredit,
} from './types'

import {
  HOME_ACTIVE_CONFIG_REDUCER_NAME,
  HOME_ACTIVE_CONFIG_ENERGY_REDUCER_NAME,
  HOME_ACTIVE_CONFIG_ENERGY_DATA_REDUCER_NAME,
  HOME_AVAILABLE_LICENSES_LIST_REDUCER_NAME,
  HOME_AVAILABLE_SOFTWARE_REDUCER_NAME,
  HOME_JOB_REDUCER_NAME,
  HOME_REDUCER_NAME,
  SELECTED_HOME_REDUCER_NAME,
  SUBSCRIPTIONS_LIST_REDUCER_NAME,
  SUBSCRIPTIONS_CREDIT_REDUCER_NAME,
  SUBSCRIPTIONS_HISTORY_REDUCER_NAME,
  PURCHASE_REQUEST_REDUCER_NAME,
  RENEW_SUBSCRIPTION_REDUCER_NAME,
  MIGRATE_SUBSCRIPTION_REDUCER_NAME,
  HOME_CONFIG_REDUCER_NAME,
  HOME_SOFTWARE_HISTORY_REDUCER_NAME,
  HOME_SOFTWARE_REDUCER_NAME,
  SUBSCRIPTION_HOME_PRICE,
  SUBSCRIPTION_PRICES,
  SUBSCRIPTION_PERIOD_END,
} from './consts'
import { RootState } from 'app/store'

import {
  CONNECTED_APPS_REDUCER_NAME,
  PARTNERS_INBOUND_LIST_REDUCER_NAME,
  PARTNERS_LIST_REDUCER_NAME,
} from './reducer'
import { SUPPORTED_APPS } from './components/HomeStatus/SubInfo/ConnectedApps/consts'
import { PartnerShape } from './components/HomeStatus/SubInfo/ConnectedApps/types'
import { DATETIME_SHORT, formatTime } from 'app/utils/formatDate'
import { createDeepEqualSelector, mapStripeSubscriptionToPlan } from './utils'

export const selectedHomeSelector = makeTypedFetchSelectors<Home>(
  HOME_REDUCER_NAME,
  SELECTED_HOME_REDUCER_NAME
)

export const activeSubscriptionSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  SUBSCRIPTIONS_LIST_REDUCER_NAME
)

export const subscriptionHomePriceSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  SUBSCRIPTION_HOME_PRICE
)

export const subscriptionPeriodEndSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  SUBSCRIPTION_PERIOD_END
)

export const userSubscriptionPlanSelector = createSelector(
  activeSubscriptionSelector.data,
  mapStripeSubscriptionToPlan
)

export const homeActiveConfigSelector =
  makeTypedFetchSelectors<HomeActiveConfig>(
    HOME_REDUCER_NAME,
    HOME_ACTIVE_CONFIG_REDUCER_NAME
  )

export const homeActiveConfigEnergySelector =
  makeTypedFetchSelectors<HomeActiveConfig>(
    HOME_REDUCER_NAME,
    HOME_ACTIVE_CONFIG_ENERGY_REDUCER_NAME
  )

export const homeActiveConfigEnergyDataSelector =
  makeTypedFetchSelectors<homeActiveEnergyConfig>(
    HOME_REDUCER_NAME,
    HOME_ACTIVE_CONFIG_ENERGY_DATA_REDUCER_NAME
  )

export const subscriptionsCreditSelector =
  makeTypedFetchSelectors<SubscriptionCredit>(
    HOME_REDUCER_NAME,
    SUBSCRIPTIONS_CREDIT_REDUCER_NAME
  )

export const homeJobSelector = makeTypedFetchSelectors<DomainType>(
  HOME_REDUCER_NAME,
  HOME_JOB_REDUCER_NAME
)

export const homeJobConfigsSelector = createSelector(
  homeJobSelector.data,
  (domain) => domain?.configs
)

export const matchingHomeConfigsSelector = createSelector(
  homeJobConfigsSelector,
  selectedHomeSelector.data,
  (homeJobConfigs, selectedHome) =>
    homeJobConfigs?.filter((config) => config.hostUid === selectedHome?.uid)
)

export const notMatchingHomeConfigsSelector = createSelector(
  selectedHomeSelector.data,
  homeJobConfigsSelector,
  (selectedHome, homeJobConfigs) => {
    return homeJobConfigs?.filter(
      (config) => config.hostUid !== selectedHome?.uid
    )
  }
)

export const homeAvailableSoftwareSelector = makeTypedFetchSelectors<
  HomeSoftwareType[]
>(
  HOME_REDUCER_NAME,
  HOME_SOFTWARE_REDUCER_NAME,
  HOME_AVAILABLE_SOFTWARE_REDUCER_NAME
)

export const homeAvailableSoftwareSortedByVersionSelector = createSelector(
  homeAvailableSoftwareSelector.data,
  (software) => {
    const copy = [...(software ?? [])]

    return copy.sort((a, b) => b.version.number - a.version.number)
  }
)

export const homeSoftwareHistoryResponseSelector = makeTypedFetchSelectors<
  HomeSoftwareHistoryType[]
>(
  HOME_REDUCER_NAME,
  HOME_SOFTWARE_REDUCER_NAME,
  HOME_SOFTWARE_HISTORY_REDUCER_NAME
)

export const homeSoftwareHistorySelector = createSelector(
  homeSoftwareHistoryResponseSelector.data,
  (softwareHistoryResponse) => softwareHistoryResponse ?? []
)

export const homeJobNameSelector = createSelector(
  homeJobSelector.data,
  (job) => job?.name ?? ''
)

export const homeSoftwareVersionSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => {
    let softwareVersionLabel = selectedHome?.softwareInfo?.version?.name || ''
    const createdDate = selectedHome?.softwareInfo?.created

    if (softwareVersionLabel.length) {
      return { softwareVersionLabel, createdDate }
    }

    const version = selectedHome?.softwareInfo?.version
    const major = version?.major
    const minor = version?.minor
    const maintenance = version?.maintenance
    const build = version?.build

    if (major && minor && maintenance && build) {
      softwareVersionLabel = `${major}.${minor}.${maintenance}.${build}`
      return { softwareVersionLabel, createdDate }
    } else {
      return {
        softwareVersionText: 'Version is not available',
        createdDate: null,
      }
    }
  }
)

export const homeAddedLicensesSelector = createSelector(
  selectedHomeSelector.data,
  (selectedHome) => selectedHome?.licenses as LicenseType[]
)

export const availableLicensesSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  HOME_AVAILABLE_LICENSES_LIST_REDUCER_NAME
)

export const licensesGroupsSelector = createSelector(
  availableLicensesSelector.data,
  (licensesData) => {
    const licenses = licensesData || []
    const groups = _groupBy(licenses, (license) => license.name)

    return _sortBy(groups, licensesDefaultSort)
  }
)

export const subscriptionHistoryIsFetching = createSelector(
  (store: RootState) =>
    store[HOME_REDUCER_NAME][SUBSCRIPTIONS_HISTORY_REDUCER_NAME]?.isFetching,
  (isFetching: any) => isFetching
)

export const subscriptionsHistorySelector = createSelector(
  (store: RootState) =>
    store[HOME_REDUCER_NAME][SUBSCRIPTIONS_HISTORY_REDUCER_NAME]?.data,
  (data: any) =>
    data?.map((subscription: any) => {
      return {
        date: +subscription.date,
        planName: subscription.priceName,
        planId: subscription.id,
        paymentInfo: subscription.paymentInfo,
        canceledAt: subscription.canceledAt
          ? formatTime(subscription.canceledAt, DATETIME_SHORT)
          : '-',
        priceId: subscription.priceId,
        quantity: subscription.quantity,
        event: subscription.event,
        description: subscription.description,
      }
    }) ?? []
)

export const partnersSelectors = makeTypedFetchSelectors<PartnerShape[]>(
  HOME_REDUCER_NAME,
  CONNECTED_APPS_REDUCER_NAME,
  PARTNERS_LIST_REDUCER_NAME
)

export const partnersInboundSelectors = makeTypedFetchSelectors<PartnerShape[]>(
  HOME_REDUCER_NAME,
  CONNECTED_APPS_REDUCER_NAME,
  PARTNERS_INBOUND_LIST_REDUCER_NAME
)

export const connectedAppsListHasLoaded = createSelector(
  [partnersSelectors.isFinished, partnersInboundSelectors.isFinished],
  (partnersIsFinished, partnersInboundIsFinish) =>
    partnersIsFinished && partnersInboundIsFinish
)

export const connectedAppsListIsLoading = createSelector(
  [partnersSelectors.isFetching, partnersInboundSelectors.isFetching],
  (isPartnersLoading, isPartnersInboundLoading) =>
    isPartnersLoading && isPartnersInboundLoading
)

export const connectedAppsListSelector = createDeepEqualSelector(
  [partnersSelectors.data, partnersInboundSelectors.data],
  (partners, partnersInbound) => {
    partners = partners || []
    partnersInbound = partnersInbound || []
    const allPartners = partners.concat(partnersInbound)
    const supportedApps = _cloneDeep(SUPPORTED_APPS)

    return supportedApps.map((app) => {
      const partner = allPartners.find(
        (partner: PartnerShape) => partner.name === app.name
      )
      if (partner?.active) {
        app.active = !!partner?.active
      }

      return app
    })
  }
)

export const purchaseRequestSelectors = makeFetchSelectors(
  HOME_REDUCER_NAME,
  PURCHASE_REQUEST_REDUCER_NAME
)

export const migrateSubscriptionSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  MIGRATE_SUBSCRIPTION_REDUCER_NAME
)

export const renewSubscriptionSelector = makeFetchSelectors(
  HOME_REDUCER_NAME,
  RENEW_SUBSCRIPTION_REDUCER_NAME
)

export const homeConfigSelectors = makeTypedFetchSelectors<HostConfig>(
  HOME_REDUCER_NAME,
  HOME_CONFIG_REDUCER_NAME
)

export const subscriptionPricesSelector = makeTypedFetchSelectors<PriceV2[]>(
  HOME_REDUCER_NAME,
  SUBSCRIPTION_PRICES
)

export const subscriptionApDevicePriceSelector = createSelector(
  subscriptionPricesSelector.data,
  (prices) => {
    return prices?.find((x) => {
      return x.nickname.includes('Access Points')
    })
  }
)

export const subscriptionEssentialsPriceSelector = (type: string) =>
  createSelector(subscriptionPricesSelector.data, (prices) => {
    return prices?.find((x) => {
      return x.product.name === 'Savant Essentials'
    })
  })

export const subscriptionByTypePriceSelector = (type: string) =>
  createSelector(subscriptionPricesSelector.data, (prices) => {
    return prices?.find((x) => {
      return x.nickname?.includes(type)
    })
  })

export const networkSubscriptionStatusSelector = (subscribedCount: number) =>
  createSelector(activeSubscriptionSelector.data, (activeSubscription) => {
    const renewingAPBuilder: number[] = []
    console.log(activeSubscription)
    try {
      if (activeSubscription !== null && activeSubscription !== undefined) {
        if (activeSubscription.cancel_at_period_end === false) {
          renewingAPBuilder.push(subscribedCount)
          renewingAPBuilder.push(subscribedCount)
        } else if (
          activeSubscription.cancel_at_period_end === true &&
          (activeSubscription.schedule === null ||
            activeSubscription.schedule === undefined)
        ) {
          renewingAPBuilder.push(subscribedCount)
          renewingAPBuilder.push(0)
        } else {
          if (
            activeSubscription.schedule.phases !== null &&
            activeSubscription.schedule.phases !== undefined
          ) {
            activeSubscription.schedule.phases.forEach(function (value: any) {
              if (value !== null) {
                renewingAPBuilder.push(value.items[0].quantity)
              }
            })
          } else {
            renewingAPBuilder.push(subscribedCount)
            renewingAPBuilder.push(subscribedCount)
          }
        }
        return renewingAPBuilder
      } else {
        renewingAPBuilder.push(subscribedCount)
        renewingAPBuilder.push(subscribedCount)
        return renewingAPBuilder
      }
    } catch (err) {
      return [0, 0]
    }
  })

export const subscriptionStatusSelector = (
  subscribedEssentialsCount: number,
  subscribedNetworkCount: number
) =>
  createSelector(activeSubscriptionSelector.data, (activeSubscription) => {
    const renewingAPBuilder: number[] = []
    const essentials: number[] = []
    const network: number[] = []
    try {
      if (
        activeSubscription.cancel_at_period_end === false &&
        activeSubscription.schedule === null
      ) {
        essentials.push(subscribedEssentialsCount)
        essentials.push(subscribedEssentialsCount)
        network.push(subscribedNetworkCount)
        network.push(subscribedNetworkCount)
      } else if (
        activeSubscription !== null &&
        activeSubscription !== undefined
      ) {
        if (
          activeSubscription.schedule === null ||
          activeSubscription.schedule === undefined
        ) {
          essentials.push(subscribedEssentialsCount)
          essentials.push(0)
          network.push(subscribedNetworkCount)
          network.push(0)
        } else {
          if (
            activeSubscription.schedule.phases !== null &&
            activeSubscription.schedule.phases !== undefined
          ) {
            activeSubscription.schedule.phases.forEach(function (phase: any) {
              if (phase !== null) {
                phase.items.forEach(function (item: any) {
                  if (item.price.nickname === 'NAI - Access Points') {
                    network.push(item.quantity)
                  } else {
                    essentials.push(item.quantity)
                  }
                })
              }
            })
          } else {
            essentials.push(subscribedEssentialsCount)
            essentials.push(subscribedEssentialsCount)
            network.push(subscribedNetworkCount)
            network.push(subscribedNetworkCount)
          }
        }
      } else {
        essentials.push(subscribedEssentialsCount)
        essentials.push(subscribedEssentialsCount)
        network.push(subscribedNetworkCount)
        network.push(subscribedNetworkCount)
      }

      if (essentials.length === 2) {
        renewingAPBuilder.push(essentials[0])
        renewingAPBuilder.push(essentials[1])
      } else {
        renewingAPBuilder.push(subscribedEssentialsCount)
        renewingAPBuilder.push(0)
      }

      if (network.length === 2) {
        renewingAPBuilder.push(network[0])
        renewingAPBuilder.push(network[1])
      } else {
        renewingAPBuilder.push(subscribedNetworkCount)
        renewingAPBuilder.push(0)
      }

      return renewingAPBuilder
    } catch (err) {
      return renewingAPBuilder
    }
  })
